import * as React from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { createStyles, makeStyles } from '@material-ui/core';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { Grid } from '@material-ui/core';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { Button } from '@progress/kendo-react-buttons';
import StringResourceManager, { GetString } from './StringResourceManager';
import { ApplicationState } from '../store';
import ListBox from './ListBox';
import {
  getNodeGroupByDivisionAndSiteNumber,
  getLaneNumberByDivisionSiteNumberAndCluster,
  getSiteNumberByDivision,
} from '../store/Assets/AssetsActions';
import {
  SendToDivisionCommandInfo,
  SendToLanesCommandInfo,
  SendToNodeGroupsCommandInfo,
  SendToSiteNumbersCommandInfo,
} from '../Interfaces/Commands/CommandInfo';
import { fetchReferenceWeightsFilePath } from '../store/ReferenceWeights/ReferenceWeightsActions';
import { STORE_CLUSTER_LANE_SELECTOR_COMMANDS } from '../utils/constants';
import enableIcon from '../assets/images/arrow-right.png';
import DynamicDropDownList from './DynamicDropDownList';

const useStyles = makeStyles(theme =>
  createStyles({
    gridAlign: {
      display: 'flex',
    },
    margin: {
      margin: theme.spacing(1),
    },
    dialogLabel: {
      fontSize: '1.1rem',
      display: 'inline',
    },
    arrowIcon: {
      height: '50px',
      weight: '50px',
    },
    iconGrid: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: 10,
    },
    listBoxGrid: {
      width: '260px',
    },
    listBox: {
      border: '1px solid #000000',
    },
    dropdownStyle: {
      marginTop: '25px',
      marginLeft: '25px',
    },
    buttonStyle: {
      alignItems: 'flex-end',
      margin: '25px',
    },
    enableButton: {
      width: '180px',
      border: '1px solid #000000',
    },
    disableButton: {
      width: '180px',
    },
  }),
);

interface Props {
  title?: string;
  command: string;
  setSendToSiteNumberCommand: (
    commandInfo: SendToSiteNumbersCommandInfo,
  ) => void;
  setSendToNodeGroupsCommand: (
    commandInfo: SendToNodeGroupsCommandInfo,
  ) => void;
  setSendToLanesCommand: (commandInfo: SendToLanesCommandInfo) => void;
  commandDropdownlistEnable?: boolean;
  commandDropdownlistData?: string[];
  buttonTitleKey?: string;
  disabled?: boolean;
  setSendToDivisionCommand: (commandInfo: SendToDivisionCommandInfo) => void;
  resetSelectionAfterSentSuccess?: boolean;
  laneNumberSelectionFilter?: string[];
}

const NodeSelection: React.FC<Props> = ({
  title,
  command,
  setSendToSiteNumberCommand,
  setSendToNodeGroupsCommand,
  setSendToLanesCommand,
  commandDropdownlistEnable,
  commandDropdownlistData,
  buttonTitleKey,
  disabled,
  setSendToDivisionCommand,
  resetSelectionAfterSentSuccess,
  laneNumberSelectionFilter,
}): React.ReactElement => {
  const classes = useStyles();

  const dispatch: Dispatch<any> = useDispatch();

  const divisions: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.divisions,
  );

  const siteNumbersByDivision: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.siteNumberByDivision,
  );

  const clustersByDivisionAndSiteNumber: string[] = useSelector(
    (state: ApplicationState) =>
      state.assetsReducer.nodeGroupByDivisionAndSiteNumber,
  );

  const laneNumberByDivisionStoreAndCluster: string[] = useSelector(
    (state: ApplicationState) =>
      state.assetsReducer.laneNumberByDivisionSiteNumberAndCluster,
  );

  const [selectedDivision, setSelectedDivision] = React.useState<string>('');

  const [selectedDivisions, setSelectedDivisions] = React.useState<string[]>(
    [],
  );

  const [selectedStores, setSelectedStores] = React.useState<string[]>([]);

  const [selectedLanes, setSelectedLanes] = React.useState<string[]>([]);

  const [selectedClusters, setSelectedClusters] = React.useState<string[]>([]);

  const [selectedStore, setSelectedStore] = React.useState<string>('');

  const [selectedCluster, setSelectedCluster] = React.useState<string>('');

  const [selectedCommand, setSelectedCommand] = React.useState<string>(command);

  const divisionCheckBoxOnChange = (value: any) => {
    const updatedSelectedItems = selectedDivisions.includes(value)
      ? selectedDivisions.filter(item => item !== value)
      : [...selectedDivisions, value];

    setSelectedDivisions(updatedSelectedItems);

    if (updatedSelectedItems.length === 1) {
      setSelectedDivision(updatedSelectedItems[0]);
      getSiteNumberByDivision(dispatch, updatedSelectedItems[0]);
    } else if (updatedSelectedItems.length > 1) {
      setSelectedStores([]);
    } else {
      setSelectedStores([]);
    }
  };

  const storeCheckBoxOnChange = (value: any) => {
    const updatedSelectedItems: any = selectedStores.includes(value)
      ? selectedStores.filter(item => item !== value)
      : [...selectedStores, value];

    setSelectedStores(updatedSelectedItems);
    if (updatedSelectedItems.length === 1) {
      setSelectedStore(updatedSelectedItems[0]);
      getNodeGroupByDivisionAndSiteNumber(
        dispatch,
        selectedDivision,
        updatedSelectedItems[0],
      );
    }
  };

  const clusterCheckBoxOnChange = (value: any) => {
    const updatedSelectedItems = selectedClusters.includes(value)
      ? selectedClusters.filter(item => item !== value)
      : [...selectedClusters, value];

    setSelectedClusters(updatedSelectedItems);
    setSelectedLanes([]);

    if (updatedSelectedItems.length === 1) {
      setSelectedCluster(updatedSelectedItems[0]);
      getLaneNumberByDivisionSiteNumberAndCluster(
        dispatch,
        selectedDivision,
        selectedStore,
        updatedSelectedItems[0],
      );
    }
  };

  const laneCheckBoxOnChange = (value: any) => {
    const updatedSelectedItems = selectedLanes.includes(value)
      ? selectedLanes.filter(item => item !== value)
      : [...selectedLanes, value];

    setSelectedLanes(updatedSelectedItems);
  };

  const handleToggleSelectAllDivision = () => {
    if (selectedDivisions.length === divisions?.length) {
      setSelectedDivisions([]);
    } else {
      setSelectedDivisions(divisions || []);
      setSelectedStores([]);
    }
  };

  const handleToggleSelectAllStore = () => {
    if (selectedStores.length === siteNumbersByDivision?.length) {
      setSelectedStores([]);
    } else {
      setSelectedStores(siteNumbersByDivision || []);
    }
  };

  const handleToggleSelectAllClusters = () => {
    if (selectedClusters.length === clustersByDivisionAndSiteNumber?.length) {
      setSelectedClusters([]);
    } else {
      setSelectedClusters(clustersByDivisionAndSiteNumber || []);
    }
  };

  const handleToggleSelectAllLanes = () => {
    if (selectedLanes.length === laneNumberByDivisionStoreAndCluster?.length) {
      setSelectedLanes([]);
    } else {
      setSelectedLanes(laneNumberByDivisionStoreAndCluster || []);
    }
  };

  const divisionArrowButtonClick = (value: any) => {
    getSiteNumberByDivision(dispatch, value);
    setSelectedStores([]);
  };

  const storeArrowButtonClick = (value: any) => {
    getNodeGroupByDivisionAndSiteNumber(dispatch, selectedDivision, value);
    setSelectedLanes([]);
  };

  const clusterArrowButtonClick = (value: any) => {
    setSelectedLanes([]);
    getLaneNumberByDivisionSiteNumberAndCluster(
      dispatch,
      selectedDivision,
      selectedStore,
      value,
    );
  };

  React.useEffect(() => {
    if (selectedStores.length === 0 || selectedStores.length > 1) {
      setSelectedLanes([]);
      setSelectedClusters([]);
    }
  }, [selectedStores]);

  React.useEffect(() => {
    if (disabled) setSelectedStores([]);
  }, [disabled]);

  const commandOnchange = (event: DropDownListChangeEvent) => {
    fetchReferenceWeightsFilePath(dispatch);
    setSelectedCommand(event.value);
  };

  const getCommand = (command: string) => {
    switch (command) {
      case STORE_CLUSTER_LANE_SELECTOR_COMMANDS.SET_ALL_WEIGHTS_TO_DEFAULT:
        return 'SetAllWeightRecordsToDefault';
      case STORE_CLUSTER_LANE_SELECTOR_COMMANDS.APPLY_NBC_SNAPSHOT:
        return 'ApplyNBCSnapshot';
      case STORE_CLUSTER_LANE_SELECTOR_COMMANDS.APPLY_CONFIGURATION:
        return 'ApplyConfigFeatures';
      case STORE_CLUSTER_LANE_SELECTOR_COMMANDS.REQUEST_LOGS:
        return 'UploadLogs';
      case STORE_CLUSTER_LANE_SELECTOR_COMMANDS.SCHEDULE_UPLOAD_LOGS:
        return 'ScheduleUploadLogs';
      case STORE_CLUSTER_LANE_SELECTOR_COMMANDS.CONFIG_ACCESS_CONTROL:
        return 'ConfigAccessControl';
      case STORE_CLUSTER_LANE_SELECTOR_COMMANDS.SCHEDULE_SET_ALL_WEIGHTS_TO_DEFAULT:
        return 'ScheduleSetAllWeightRecordsToDefault';
        case STORE_CLUSTER_LANE_SELECTOR_COMMANDS.SCHEDULE_CONFIG_ACCESS_CONTROL:
        return 'ScheduleConfigAccessControl';
      default:
        return '';
    }
  };

  const sendButtonClick = () => {
    if (selectedDivisions.length > 0 && selectedStores.length == 0) {
      const command: SendToDivisionCommandInfo = {
        Divisions: selectedDivisions,
        CommandInfo: {
          Command: getCommand(selectedCommand),
        },
      };

      setSendToDivisionCommand(command);
    } else if (
      selectedDivisions.length == 1 &&
      selectedStores.length > 0 &&
      selectedClusters.length == 0
    ) {
      const command: SendToSiteNumbersCommandInfo = {
        Division: selectedDivision,
        SiteNumbers: selectedStores,
        CommandInfo: {
          Command: getCommand(selectedCommand),
        },
      };

      setSendToSiteNumberCommand(command);
    } else if (
      selectedDivisions.length == 1 &&
      selectedStores.length == 1 &&
      selectedClusters.length > 0 &&
      selectedLanes.length == 0
    ) {
      const command: SendToNodeGroupsCommandInfo = {
        Division: selectedDivision,
        SiteNumber: selectedStore,
        NodeGroups: selectedClusters,
        CommandInfo: {
          Command: getCommand(selectedCommand),
        },
      };

      setSendToNodeGroupsCommand(command);
    } else if (
      selectedDivisions.length == 1 &&
      selectedStores.length == 1 &&
      selectedClusters.length == 1 &&
      selectedLanes.length > 0
    ) {
      const command: SendToLanesCommandInfo = {
        Division: selectedDivision,
        SiteNumber: selectedStore,
        NodeGroup: selectedCluster,
        LaneNumbers: selectedLanes,
        CommandInfo: {
          Command: getCommand(selectedCommand),
        },
      };

      setSendToLanesCommand(command);
    }
  };

  const resetSelection = () => {
    setSelectedDivisions([]);
    setSelectedLanes([]);
    setSelectedClusters([]);
    setSelectedStores([]);
  };

  React.useEffect(() => {
    if (resetSelectionAfterSentSuccess) resetSelection();
  }, [resetSelectionAfterSentSuccess]);

  return (
    <React.Fragment>
      <Grid className={classes.margin}>
        <StringResourceManager
          variant="body1"
          style="commandLabel"
          stringKey={title}
          gutterBottom={true}
        />
      </Grid>
      <Grid className={clsx(classes.gridAlign, classes.margin)}>
        <Grid className={classes.listBoxGrid}>
          <Grid>
            <StringResourceManager
              variant="body1"
              style="dialogLabel"
              stringKey={'SELECT_DIVISION'}
              gutterBottom={true}
            />
          </Grid>
          <Grid className={classes.listBox}>
            <ListBox
              data={divisions}
              buttonEnable={true}
              handleCheckBoxEnable={divisionCheckBoxOnChange}
              selectedItems={selectedDivisions}
              handleToggleSelectAll={handleToggleSelectAllDivision}
              arrowButtonClick={divisionArrowButtonClick}
              disabled={disabled}
            />
          </Grid>
        </Grid>
        <Grid
          className={classes.iconGrid}
          hidden={selectedDivisions.length === 1 ? false : true}
        >
          <img src={enableIcon} className={classes.arrowIcon} />
        </Grid>
        <Grid
          className={classes.listBoxGrid}
          hidden={selectedDivisions.length === 1 ? false : true}
        >
          <Grid>
            <StringResourceManager
              variant="body1"
              style="dialogLabel"
              stringKey={'SELECT_STORES'}
              gutterBottom={true}
            />
          </Grid>
          <Grid className={classes.listBox}>
            <ListBox
              data={siteNumbersByDivision}
              buttonEnable={true}
              handleCheckBoxEnable={storeCheckBoxOnChange}
              selectedItems={selectedStores}
              handleToggleSelectAll={handleToggleSelectAllStore}
              arrowButtonClick={storeArrowButtonClick}
              disabled={selectedDivisions.length > 1 ? true : false}
            />
          </Grid>
        </Grid>
        <Grid
          className={classes.iconGrid}
          hidden={selectedStores.length === 1 ? false : true}
        >
          <img src={enableIcon} className={classes.arrowIcon} />
        </Grid>
        <Grid
          className={classes.listBoxGrid}
          hidden={selectedStores.length === 1 ? false : true}
        >
          <Grid>
            <StringResourceManager
              variant="body1"
              style="dialogLabel"
              stringKey={'SELECT_CLUSTERS'}
              gutterBottom={true}
            />
          </Grid>
          <Grid className={classes.listBox}>
            <ListBox
              data={clustersByDivisionAndSiteNumber}
              buttonEnable={true}
              handleCheckBoxEnable={clusterCheckBoxOnChange}
              handleToggleSelectAll={handleToggleSelectAllClusters}
              selectedItems={selectedClusters}
              disabled={selectedStores.length > 1 ? true : false}
              arrowButtonClick={clusterArrowButtonClick}
            />
          </Grid>
        </Grid>
        <Grid
          className={classes.iconGrid}
          hidden={selectedClusters.length === 1 ? false : true}
        >
          <img src={enableIcon} className={classes.arrowIcon} />
        </Grid>
        <Grid
          className={classes.listBoxGrid}
          hidden={selectedClusters.length === 1 ? false : true}
        >
          <Grid>
            <StringResourceManager
              variant="body1"
              style="dialogLabel"
              stringKey={'SELECT_LANES'}
              gutterBottom={true}
            />
          </Grid>
          <Grid className={classes.listBox}>
            <ListBox
              data={
                laneNumberSelectionFilter &&
                laneNumberSelectionFilter.length > 0
                  ? laneNumberByDivisionStoreAndCluster.filter(node =>
                      laneNumberSelectionFilter.includes(node),
                    )
                  : laneNumberByDivisionStoreAndCluster
              }
              handleCheckBoxEnable={laneCheckBoxOnChange}
              handleToggleSelectAll={handleToggleSelectAllLanes}
              selectedItems={selectedLanes}
              disabled={
                selectedStores.length > 1 ||
                selectedClusters.length > 1 ||
                selectedDivisions.length > 1
                  ? true
                  : false
              }
            />
          </Grid>
        </Grid>
        {commandDropdownlistEnable && commandDropdownlistEnable != undefined ? (
          <Grid className={classes.listBoxGrid}>
            <Grid className={classes.dropdownStyle}>
              <DynamicDropDownList
                data={commandDropdownlistData}
                defaultValue={GetString(command)}
                disabled={selectedDivisions.length > 0 ? false : true}
                onChange={commandOnchange}
                width={250}
              />
            </Grid>
          </Grid>
        ) : null}
        <Grid className={classes.buttonStyle}>
          <Button
            className={
              selectedStores.length > 0
                ? classes.enableButton
                : classes.disableButton
            }
            disabled={selectedDivisions.length > 0 ? false : true}
            onClick={sendButtonClick}
          >
            <StringResourceManager
              stringKey={
                buttonTitleKey == undefined ? 'SEND_BUTTON' : buttonTitleKey
              }
            />
          </Button>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default NodeSelection;
